import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import Mousetrap from "mousetrap";
import "mousetrap/plugins/global-bind/mousetrap-global-bind";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  KanbanDefaultStates,
  STATE_KEY_TO_STATUS_ID,
} from "@/components/ProjectList/CanvasKanban";
import { useFocusStore } from "@/store/zustand/focusStore";
import { v4 as uuidv4 } from "uuid";

interface IssueKeyboardShortcutsProps {
  canvasIdToPrd: Record<string, any>;
  stateToCanvases: Record<string, any>;
  sortColumnIssues: (issues: any[], canvasIdToPrd: any) => any[];
  canvasesToShow: any[];
  selectedIssueIds: string[];
  focusedIssueId: string | null;
  viewType: "grid" | "list";
  isEnabled?: boolean; // Add ability to disable shortcuts when needed
}

// Global flag to temporarily disable mouse hover focus during keyboard operations
let isKeyboardOperationActive = false;

// Function to temporarily disable mouse hover focus
const setKeyboardOperationActive = (active: boolean) => {
  isKeyboardOperationActive = active;
  if (active) {
    // Clear the flag after a reasonable delay to allow keyboard operation to complete
    setTimeout(() => {
      isKeyboardOperationActive = false;
    }, 200); // 200ms should be enough for the keyboard operation + focus restoration
  }
};

// Export function so components can check if they should ignore mouse hover
export const shouldIgnoreMouseHover = () => isKeyboardOperationActive;

export const useIssueKeyboardShortcuts = ({
  canvasIdToPrd,
  stateToCanvases,
  sortColumnIssues,
  canvasesToShow,
  selectedIssueIds,
  focusedIssueId,
  viewType,
  isEnabled = true,
}: IssueKeyboardShortcutsProps) => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const dispatch = useDispatch();

  const { setFocusedIssue } = useFocusStore();

  // Get the ordered list of column statuses for navigation
  const getColumnOrder = useCallback(() => {
    return Object.keys(KanbanDefaultStates);
  }, []);

  // Helper function to check if all issues have the same status
  const allIssuesHaveSameStatus = useCallback(
    (issueIds: string[]) => {
      if (issueIds.length <= 1) return true;

      const statuses = issueIds
        .map((id) => {
          const issue = canvasesToShow.find((canvas) => canvas.id === id);
          if (!issue) return null;
          const prd = canvasIdToPrd[id];
          return prd?.status || KanbanDefaultStates.BACKLOG;
        })
        .filter((status) => status !== null);

      // Check if all statuses are the same
      const uniqueStatuses = new Set(statuses);
      return uniqueStatuses.size <= 1;
    },
    [canvasesToShow, canvasIdToPrd]
  );

  // Move issues up or down within their current column
  const moveIssuesVertically = useCallback(
    async (issueIds: string[], direction: "up" | "down") => {
      if (!supabaseClient || issueIds.length === 0) return;

      // Don't allow movement if selected issues are in different statuses
      if (!allIssuesHaveSameStatus(issueIds)) {
        console.log(
          "Cannot move issues: selected issues are in different status columns"
        );
        return;
      }

      try {
        const prdsToUpdate: any[] = [];
        const processedColumns = new Set<string>();

        for (const issueId of issueIds) {
          const issue = canvasesToShow.find((canvas) => canvas.id === issueId);
          if (!issue) continue;

          const prd = canvasIdToPrd[issueId];
          const currentStatus = prd?.status || KanbanDefaultStates.BACKLOG;

          // Only process each column once to avoid duplicate updates
          if (processedColumns.has(currentStatus)) continue;
          processedColumns.add(currentStatus);

          // Get all issues in the current column
          const currentColumnIssues = sortColumnIssues(
            canvasesToShow.filter((canvas) => {
              const canvasPrd = canvasIdToPrd[canvas.id];
              return (
                (canvasPrd?.status || KanbanDefaultStates.BACKLOG) ===
                currentStatus
              );
            }),
            canvasIdToPrd
          );

          // Find which issues in this column are being moved
          const movingIssuesInColumn = issueIds.filter((id) =>
            currentColumnIssues.some((canvas) => canvas.id === id)
          );

          if (movingIssuesInColumn.length === 0) continue;

          // Create a reordered list
          let reorderedIssues = [...currentColumnIssues];

          // Improved logic for handling multiple selected issues
          if (movingIssuesInColumn.length === 1) {
            // Single issue - use simple logic
            const movingIssueId = movingIssuesInColumn[0];
            const currentIndex = reorderedIssues.findIndex(
              (canvas) => canvas.id === movingIssueId
            );

            if (currentIndex !== -1) {
              let newIndex: number;
              if (direction === "up") {
                newIndex = Math.max(0, currentIndex - 1);
              } else {
                newIndex = Math.min(
                  reorderedIssues.length - 1,
                  currentIndex + 1
                );
              }

              // Only move if there's actual change
              if (newIndex !== currentIndex) {
                const [movedIssue] = reorderedIssues.splice(currentIndex, 1);
                reorderedIssues.splice(newIndex, 0, movedIssue);
              }
            }
          } else {
            // Multiple issues - move as a group while maintaining relative order
            const movingIssuesWithIndices = movingIssuesInColumn
              .map((id) => ({
                id,
                currentIndex: reorderedIssues.findIndex(
                  (canvas) => canvas.id === id
                ),
                canvas: reorderedIssues.find((canvas) => canvas.id === id),
              }))
              .filter((item) => item.currentIndex !== -1 && item.canvas)
              .sort((a, b) => a.currentIndex - b.currentIndex);

            if (movingIssuesWithIndices.length === 0) continue;

            // Find the target position for the group
            let targetIndex: number;
            if (direction === "up") {
              // Move group up: target is one position above the topmost selected issue
              const topIndex = movingIssuesWithIndices[0].currentIndex;
              targetIndex = Math.max(0, topIndex - 1);
            } else {
              // Move group down: find the first non-selected item after the group
              const bottomIndex =
                movingIssuesWithIndices[movingIssuesWithIndices.length - 1]
                  .currentIndex;
              const selectedIds = new Set(movingIssuesInColumn);

              // Find the next non-selected item after the bottom of the group
              let nextNonSelectedIndex = bottomIndex + 1;
              while (
                nextNonSelectedIndex < reorderedIssues.length &&
                selectedIds.has(reorderedIssues[nextNonSelectedIndex].id)
              ) {
                nextNonSelectedIndex++;
              }

              if (nextNonSelectedIndex >= reorderedIssues.length) {
                // No non-selected items after the group, move to the end
                targetIndex =
                  reorderedIssues.length - movingIssuesWithIndices.length;
              } else {
                // Move the group to just after the next non-selected item
                // After removal, the next non-selected item will be at bottomIndex + 1 - groupSize
                // So we want to insert at bottomIndex + 2 - groupSize
                targetIndex =
                  nextNonSelectedIndex - movingIssuesWithIndices.length + 1;
              }
            }

            // Remove all moving issues from their current positions (in reverse order to maintain indices)
            const movingCanvases = movingIssuesWithIndices.map(
              (item) => item.canvas
            );
            for (let i = movingIssuesWithIndices.length - 1; i >= 0; i--) {
              reorderedIssues.splice(
                movingIssuesWithIndices[i].currentIndex,
                1
              );
            }

            // Insert all moving issues at the target position (maintaining their relative order)
            reorderedIssues.splice(targetIndex, 0, ...movingCanvases);
          }

          // Update PRDs for all issues in this column with new ordering
          reorderedIssues.forEach((canvas, index) => {
            const existingPrd = canvasIdToPrd[canvas.id];
            if (existingPrd) {
              prdsToUpdate.push({
                ...existingPrd,
                order: index,
              });
            } else {
              prdsToUpdate.push({
                id: uuidv4().toString(),
                canvas_id: canvas.id,
                status: currentStatus,
                order: index,
              });
            }
          });
        }

        if (prdsToUpdate.length === 0) return;

        // Update local state first to trigger immediate UI update and animation
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: prdsToUpdate,
          deletedItems: [],
        });

        // Update database in the background
        await supabaseClient
          .from("feature_prd")
          .upsert(prdsToUpdate, { onConflict: "canvas_id" });
      } catch (error) {
        console.error(`Error moving issues ${direction}:`, error);
      }
    },
    [
      supabaseClient,
      canvasIdToPrd,
      canvasesToShow,
      sortColumnIssues,
      allIssuesHaveSameStatus,
    ]
  );

  // Move issues to adjacent column (left or right)
  const moveIssuesHorizontally = useCallback(
    async (issueIds: string[], direction: "left" | "right") => {
      if (!supabaseClient || issueIds.length === 0) return;

      // Don't allow movement if selected issues are in different statuses
      if (!allIssuesHaveSameStatus(issueIds)) return;

      try {
        const columnOrder = getColumnOrder();
        const prdsToUpdate: any[] = [];

        // First, determine the target status for all issues (they should all move to the same target)
        const firstIssue = canvasesToShow.find(
          (canvas) => canvas.id === issueIds[0]
        );
        if (!firstIssue) return;

        const firstPrd = canvasIdToPrd[issueIds[0]];
        const sourceStatus = firstPrd?.status || KanbanDefaultStates.BACKLOG;

        // Find target column
        const sourceColumnKey = Object.entries(KanbanDefaultStates).find(
          ([_, value]) => value === sourceStatus
        )?.[0];

        if (!sourceColumnKey) return;

        const sourceColumnIndex = columnOrder.indexOf(sourceColumnKey);
        if (sourceColumnIndex === -1) return;

        let targetColumnIndex: number;
        if (direction === "left") {
          targetColumnIndex = Math.max(0, sourceColumnIndex - 1);
        } else {
          targetColumnIndex = Math.min(
            columnOrder.length - 1,
            sourceColumnIndex + 1
          );
        }

        // Skip if no movement needed
        if (targetColumnIndex === sourceColumnIndex) return;

        const targetColumnKey = columnOrder[targetColumnIndex];
        const targetStatus = KanbanDefaultStates[targetColumnKey];

        // Get all issues in source column, sorted by order
        const sourceColumnIssues = sortColumnIssues(
          canvasesToShow.filter((canvas) => {
            const canvasPrd = canvasIdToPrd[canvas.id];
            return (
              (canvasPrd?.status || KanbanDefaultStates.BACKLOG) ===
              sourceStatus
            );
          }),
          canvasIdToPrd
        );

        // Get all issues currently in the target column, sorted by order
        const currentTargetColumnIssues = sortColumnIssues(
          canvasesToShow.filter((canvas) => {
            const canvasPrd = canvasIdToPrd[canvas.id];
            return (
              (canvasPrd?.status || KanbanDefaultStates.BACKLOG) ===
              targetStatus
            );
          }),
          canvasIdToPrd
        );

        // Filter to only the issues being moved, preserving their original order
        const movingIssuesInOrder = sourceColumnIssues
          .filter((canvas) => issueIds.includes(canvas.id))
          .map((canvas) => canvas.id);

        // Find the reference issue for positioning (focused issue if in selection, otherwise first issue)
        const referenceIssueId =
          focusedIssueId && movingIssuesInOrder.includes(focusedIssueId)
            ? focusedIssueId
            : movingIssuesInOrder[0];

        // Calculate the normalized index of the reference issue in the source column
        const referenceNormalizedIndex = sourceColumnIssues.findIndex(
          (canvas) => canvas.id === referenceIssueId
        );

        if (referenceNormalizedIndex === -1) return;

        // Calculate where to insert in the target column based on normalized index
        const targetInsertionIndex = Math.min(
          referenceNormalizedIndex,
          currentTargetColumnIssues.length
        );

        // Create the new target column order by inserting moved issues at the normalized position
        const newTargetColumnOrder = [...currentTargetColumnIssues];

        // Insert the moving issues at the target normalized index
        const movingCanvases = movingIssuesInOrder
          .map((id) => canvasesToShow.find((canvas) => canvas.id === id))
          .filter((canvas) => canvas !== undefined);

        newTargetColumnOrder.splice(targetInsertionIndex, 0, ...movingCanvases);

        // Create updates for all issues in the target column with consecutive order values
        newTargetColumnOrder.forEach((canvas, normalizedIndex) => {
          // Skip if this isn't one of the moving issues - we'll handle existing target issues separately
          if (!movingIssuesInOrder.includes(canvas.id)) return;

          const prd = canvasIdToPrd[canvas.id];
          if (prd) {
            prdsToUpdate.push({
              ...prd,
              status: targetStatus,
              order: normalizedIndex,
            });
          } else {
            prdsToUpdate.push({
              id: uuidv4().toString(),
              canvas_id: canvas.id,
              status: targetStatus,
              order: normalizedIndex,
            });
          }
        });

        // Update order values for existing issues in target column that need reordering
        const existingTargetIssues = newTargetColumnOrder.filter(
          (canvas) => !movingIssuesInOrder.includes(canvas.id)
        );

        existingTargetIssues.forEach((canvas, index) => {
          const prd = canvasIdToPrd[canvas.id];
          const currentOrder = prd?.order ?? 0;

          // Find this canvas's position in the new order
          const newNormalizedIndex = newTargetColumnOrder.findIndex(
            (c) => c.id === canvas.id
          );

          // Only update if the order actually changed
          if (newNormalizedIndex !== currentOrder) {
            if (prd) {
              prdsToUpdate.push({
                ...prd,
                order: newNormalizedIndex,
              });
            } else {
              prdsToUpdate.push({
                id: uuidv4().toString(),
                canvas_id: canvas.id,
                status: targetStatus,
                order: newNormalizedIndex,
              });
            }
          }
        });

        if (prdsToUpdate.length === 0) return;

        // Update local state first to trigger immediate UI update and animation
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: prdsToUpdate,
          deletedItems: [],
        });

        // Update database in the background
        await supabaseClient
          .from("feature_prd")
          .upsert(prdsToUpdate, { onConflict: "canvas_id" });
      } catch (error) {
        console.error(`Error moving issues ${direction}:`, error);
      }
    },
    [
      supabaseClient,
      canvasIdToPrd,
      canvasesToShow,
      sortColumnIssues,
      getColumnOrder,
      allIssuesHaveSameStatus,
      focusedIssueId,
    ]
  );

  // Handle reordering keyboard shortcuts
  const handleReorderUp = useCallback(
    (e: any) => {
      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
          ? [focusedIssueId]
          : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesVertically(issuesToMove, "up");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(() => setFocusedIssue(primaryIssue), 50);
      }
    },
    [selectedIssueIds, focusedIssueId, moveIssuesVertically, setFocusedIssue]
  );

  const handleReorderDown = useCallback(
    (e: any) => {
      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
          ? [focusedIssueId]
          : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesVertically(issuesToMove, "down");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(() => setFocusedIssue(primaryIssue), 50);
      }
    },
    [selectedIssueIds, focusedIssueId, moveIssuesVertically, setFocusedIssue]
  );

  const handleMoveLeft = useCallback(
    (e: any) => {
      // Only allow horizontal movement in Kanban view
      if (viewType !== "grid") return;

      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
          ? [focusedIssueId]
          : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesHorizontally(issuesToMove, "left");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(() => setFocusedIssue(primaryIssue), 50);
      }
    },
    [
      viewType,
      selectedIssueIds,
      focusedIssueId,
      moveIssuesHorizontally,
      setFocusedIssue,
    ]
  );

  const handleMoveRight = useCallback(
    (e: any) => {
      // Only allow horizontal movement in Kanban view
      if (viewType !== "grid") return;

      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
          ? [focusedIssueId]
          : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesHorizontally(issuesToMove, "right");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(() => setFocusedIssue(primaryIssue), 50);
      }
    },
    [
      viewType,
      selectedIssueIds,
      focusedIssueId,
      moveIssuesHorizontally,
      dispatch,
    ]
  );

  // Set up mousetrap bindings
  useEffect(() => {
    if (!isEnabled) return;

    // Bind keyboard shortcuts using mousetrap
    Mousetrap.bind("alt+up", handleReorderUp);
    Mousetrap.bind("alt+j", handleReorderUp);
    Mousetrap.bind("alt+down", handleReorderDown);
    Mousetrap.bind("alt+k", handleReorderDown);
    Mousetrap.bind("alt+left", handleMoveLeft);
    Mousetrap.bind("alt+right", handleMoveRight);

    // Also bind option key for macOS
    Mousetrap.bind("option+up", handleReorderUp);
    Mousetrap.bind("option+down", handleReorderDown);
    Mousetrap.bind("option+left", handleMoveLeft);
    Mousetrap.bind("option+right", handleMoveRight);

    return () => {
      // Clean up bindings
      Mousetrap.unbind("alt+up");
      Mousetrap.unbind("alt+down");
      Mousetrap.unbind("alt+left");
      Mousetrap.unbind("alt+right");
      Mousetrap.unbind("option+up");
      Mousetrap.unbind("option+down");
      Mousetrap.unbind("option+left");
      Mousetrap.unbind("option+right");
    };
  }, [
    isEnabled,
    handleReorderUp,
    handleReorderDown,
    handleMoveLeft,
    handleMoveRight,
  ]);

  return {
    moveIssuesVertically,
    moveIssuesHorizontally,
  };
};
